const DEFAULT_SOCKET_PATH = '/socket.io/';
const DEFAULT_SOCKET_TRANSPORTS = ['polling', 'websocket'];
const VALID_SOCKET_TRANSPORTS = new Set(DEFAULT_SOCKET_TRANSPORTS);
const getClientEnv = (key, fallback = '') => {
    if (typeof process !== 'undefined' && process.env && typeof process.env[key] !== 'undefined') {
        return process.env[key];
    }
    if (typeof window !== 'undefined' &&
        window.__ENV__ &&
        typeof window.__ENV__[key] !== 'undefined') {
        return window.__ENV__[key];
    }
    return fallback;
};
const normalizeSocketPath = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
        return DEFAULT_SOCKET_PATH;
    }
    const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
};
// O proxy de produção (nginx) aceita upgrade para WebSocket nativo. O fallback
// por polling continua disponível via REACT_APP_SOCKET_FORCE_POLLING.
const POLLING_ONLY_HOSTNAMES = new Set();
const LOCAL_DEV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);
const isWebpackLocalDevHost = (hostname, port) => LOCAL_DEV_HOSTNAMES.has(hostname.trim().toLowerCase()) && port === '3001';
// Hosts de IP privado (acesso via VLANs) usam WebSocket nativo: o nginx já faz o
// upgrade corretamente e long-polling forçado sobrecarregava o servidor Socket.IO.
// Rollback sem rebuild: REACT_APP_SOCKET_FORCE_POLLING=true.
//
// localhost:3001 é polling-only: webpack.dev.js proxia /socket.io para Flask :5000
// (Werkzeug), que responde 400 em transport=websocket. O processo :5500 com gevent
// não recebe esse tráfego no dev-full.
export const shouldForcePollingForHost = (_protocol, hostname, port) => {
    const normalizedHostname = hostname.trim().toLowerCase();
    if (isWebpackLocalDevHost(normalizedHostname, port)) {
        return true;
    }
    return POLLING_ONLY_HOSTNAMES.has(normalizedHostname);
};
const shouldForcePollingForCurrentHost = () => {
    if (typeof window === 'undefined') {
        return false;
    }
    return shouldForcePollingForHost(window.location.protocol, window.location.hostname, window.location.port);
};
export const normalizeSocketTransports = (value) => {
    const parsed = value
        .split(',')
        .map(entry => entry.trim().toLowerCase())
        .filter(entry => VALID_SOCKET_TRANSPORTS.has(entry));
    const unique = Array.from(new Set(parsed));
    const hasPolling = unique.includes('polling');
    const hasWebsocket = unique.includes('websocket');
    if (hasPolling && hasWebsocket) {
        return ['polling', 'websocket'];
    }
    if (hasPolling) {
        return ['polling'];
    }
    if (hasWebsocket) {
        return ['websocket'];
    }
    return [...DEFAULT_SOCKET_TRANSPORTS];
};
export const getSocketIOUrl = () => {
    const explicitUrl = getClientEnv('REACT_APP_SOCKET_BASE_URL', '').trim();
    if (explicitUrl) {
        return explicitUrl.replace(/\/$/, '');
    }
    if (typeof window !== 'undefined') {
        return window.location.origin;
    }
    return '';
};
export const getSocketIOPath = () => {
    const explicitPath = getClientEnv('REACT_APP_SOCKET_PATH', DEFAULT_SOCKET_PATH);
    return normalizeSocketPath(explicitPath);
};
export const getSocketIOTransports = () => {
    if (shouldSocketForcePolling()) {
        return ['polling'];
    }
    const configured = getClientEnv('REACT_APP_SOCKET_TRANSPORTS', DEFAULT_SOCKET_TRANSPORTS.join(','));
    return normalizeSocketTransports(configured);
};
export const shouldSocketAllowUpgrade = () => {
    if (shouldSocketForcePolling()) {
        return false;
    }
    const configured = getClientEnv('REACT_APP_SOCKET_ALLOW_UPGRADE', 'true').trim().toLowerCase();
    return configured === 'true' && getSocketIOTransports().includes('websocket');
};
export const shouldSocketForcePolling = () => {
    const configured = getClientEnv('REACT_APP_SOCKET_FORCE_POLLING', '').trim().toLowerCase();
    if (configured === 'true') {
        return true;
    }
    if (configured === 'false') {
        return shouldForcePollingForCurrentHost();
    }
    return shouldForcePollingForCurrentHost();
};
export const shouldUseWebsocketOnlyForLocalDev = (_hostname = typeof window !== 'undefined' ? window.location.hostname : '', _port = typeof window !== 'undefined' ? window.location.port : '') => {
    // Webpack local aponta /socket.io para Werkzeug :5000. Pedir websocket-only
    // gera GET /socket.io/?transport=websocket 400 em loop e derruba o SID.
    return false;
};
